import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, CheckCircle, XCircle, Clock, FileText, RefreshCw } from 'lucide-react';
import { MyFNGComplaintService } from '../../services/myfngComplaintService';

const MyFNGComplaintsList = ({ visible, onClose, userId }) => {
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  const { data: complaintsData, isLoading, refetch } = useQuery({
    queryKey: ['myfngComplaints', userId],
    queryFn: () => MyFNGComplaintService.getComplaintsByUser(userId),
    enabled: visible && !!userId,
  });

  const complaints = complaintsData?.data || [];

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle };
      case 'failed':
        return { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle };
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock };
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText };
    }
  };

  if (!visible) return null;

  return (
    <>
      {/* Main List Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
        <div className="bg-white rounded-t-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">My Registered MyFNG Complaints</h2>
              <p className="text-sm text-gray-600 mt-1">{complaints.length} complaint{complaints.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => refetch()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw size={20} className="text-gray-600" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : complaints.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <FileText size={64} className="text-gray-400 mb-4" />
                <p className="text-lg font-semibold text-gray-900 mb-2">No complaints registered yet</p>
                <p className="text-sm text-gray-600 text-center">
                  Your registered MyFNG complaints will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {complaints.map((complaint) => {
                  const statusStyle = getStatusColor(complaint.api_status);
                  const StatusIcon = statusStyle.icon;
                  
                  return (
                    <div
                      key={complaint.id}
                      onClick={() => setSelectedComplaint(complaint)}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">
                            {complaint.chat_name || 'N/A'}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2">{complaint.waba || 'N/A'}</p>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <FileText size={16} />
                              {complaint.car_model || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={16} />
                              {formatDate(complaint.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${statusStyle.bg}`}>
                          <StatusIcon size={16} className={statusStyle.text} />
                          <span className={`text-xs font-semibold ${statusStyle.text}`}>
                            {complaint.api_status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedComplaint && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Detail Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Complaint Details</h3>
              <button
                onClick={() => setSelectedComplaint(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            {/* Detail Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer Name</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.chat_name || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">WhatsApp Number</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.waba || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Car Model</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.car_model || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer Keyword/Remark</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.cust_keyword || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                  <div className="mt-1">
                    {(() => {
                      const statusStyle = getStatusColor(selectedComplaint.api_status);
                      const StatusIcon = statusStyle.icon;
                      return (
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${statusStyle.bg}`}>
                          <StatusIcon size={16} className={statusStyle.text} />
                          <span className={`text-sm font-semibold ${statusStyle.text}`}>
                            {selectedComplaint.api_status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lead Tag</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.lead_tag || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Lead Source</label>
                  <p className="text-base text-gray-900 mt-1">{selectedComplaint.lead_source || 'N/A'}</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Registered At</label>
                  <p className="text-base text-gray-900 mt-1">{formatDate(selectedComplaint.created_at)}</p>
                </div>

                {selectedComplaint.error_message && (
                  <div>
                    <label className="text-xs font-semibold text-red-500 uppercase tracking-wide">Error Message</label>
                    <p className="text-base text-red-600 mt-1">{selectedComplaint.error_message}</p>
                  </div>
                )}

                {selectedComplaint.api_response && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">API Response</label>
                    <div className="mt-1 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                        {JSON.stringify(selectedComplaint.api_response, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Detail Footer */}
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setSelectedComplaint(null)}
                className="w-full px-4 py-3 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyFNGComplaintsList;

